import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeftIcon,
  CheckIcon,
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { TrophyIcon } from '@heroicons/react/24/solid';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import type { Course } from './CourseModal';
import TeeBoxManager, { type TeeBox } from './TeeBoxManager';

interface Hole {
  id: string;
  course_id: string;
  hole_number: number;
  par: number;
  handicap_index: number | null;
  distance_meters: number | null;
  distance_yards: number | null;
  description: string | null;
  notes: string | null;
}

interface HoleTeeDistance {
  id: string;
  hole_id: string;
  tee_box_id: string;
  distance_yards: number | null;
  distance_meters: number | null;
}

interface HoleForm {
  par: number;
  handicap_index: string;
  distance_meters: string;
  distance_yards: string;
  description: string;
  notes: string;
}

interface TeeDistanceForm {
  [teeBoxId: string]: { yards: string; meters: string };
}

interface Props {
  course: Course;
  onBack: () => void;
}

const parColors: Record<number, string> = {
  3: 'bg-blue-100 text-blue-700 border-blue-200',
  4: 'bg-green-100 text-green-700 border-green-200',
  5: 'bg-amber-100 text-amber-700 border-amber-200',
  6: 'bg-red-100 text-red-700 border-red-200',
};

const COLOR_DOT: Record<string, string> = {
  black: 'bg-gray-900',
  gold: 'bg-yellow-500',
  blue: 'bg-blue-600',
  white: 'bg-white border border-gray-300',
  red: 'bg-red-500',
  green: 'bg-green-600',
  yellow: 'bg-yellow-300',
  orange: 'bg-orange-500',
};

function defaultHoleForm(): HoleForm {
  return { par: 4, handicap_index: '', distance_meters: '', distance_yards: '', description: '', notes: '' };
}

function holeToForm(hole: Hole): HoleForm {
  return {
    par: hole.par,
    handicap_index: hole.handicap_index != null ? String(hole.handicap_index) : '',
    distance_meters: hole.distance_meters != null ? String(hole.distance_meters) : '',
    distance_yards: hole.distance_yards != null ? String(hole.distance_yards) : '',
    description: hole.description ?? '',
    notes: hole.notes ?? '',
  };
}

function defaultTeeDistanceForm(teeBoxes: TeeBox[]): TeeDistanceForm {
  return Object.fromEntries(teeBoxes.map((t) => [t.id, { yards: '', meters: '' }]));
}

function HoleCard({ hole, isSelected, onClick }: { hole: Hole | null; holeNumber: number; isSelected: boolean; onClick: () => void }) {
  const parClass = hole ? (parColors[hole.par] ?? 'bg-gray-100 text-gray-700 border-gray-200') : 'bg-gray-50 text-gray-300 border-dashed border-gray-200';

  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center justify-center p-2 rounded-xl border-2 transition-all ${
        isSelected
          ? 'border-golf-orange shadow-sm shadow-golf-orange/20 bg-golf-orange/5'
          : 'border-transparent hover:border-gray-200 bg-white hover:bg-gray-50'
      }`}
    >
      <span className="text-xs text-gray-400 font-medium">{hole ? String(hole.hole_number).padStart(2, '0') : '--'}</span>
      <span className={`mt-1 text-xs font-bold px-2 py-0.5 rounded-full border ${parClass}`}>
        {hole ? `P${hole.par}` : '—'}
      </span>
      {hole?.distance_yards && (
        <span className="text-[10px] text-gray-400 mt-0.5">{hole.distance_yards}y</span>
      )}
    </button>
  );
}

type EditorTab = 'holes' | 'tees';

export default function HoleEditor({ course, onBack }: Props) {
  const [activeTab, setActiveTab] = useState<EditorTab>('holes');
  const [holes, setHoles] = useState<Hole[]>([]);
  const [teeBoxes, setTeeBoxes] = useState<TeeBox[]>([]);
  const [teeDistances, setTeeDistances] = useState<HoleTeeDistance[]>([]);
  const [selectedHole, setSelectedHole] = useState(1);
  const [form, setForm] = useState<HoleForm>(defaultHoleForm());
  const [teeForm, setTeeForm] = useState<TeeDistanceForm>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const totalHoles = course.holes_count;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [holesRes, teesRes] = await Promise.all([
        supabase.from('holes').select('*').eq('course_id', course.id).order('hole_number'),
        supabase.from('tee_boxes').select('*').eq('course_id', course.id).order('sort_order'),
      ]);
      if (holesRes.error) throw holesRes.error;
      const loadedHoles = (holesRes.data as Hole[]) ?? [];
      const loadedTees = (teesRes.data as TeeBox[]) ?? [];
      setHoles(loadedHoles);
      setTeeBoxes(loadedTees);

      if (loadedHoles.length > 0 && loadedTees.length > 0) {
        const holeIds = loadedHoles.map((h) => h.id);
        const { data: distData } = await supabase
          .from('hole_tee_distances')
          .select('*')
          .in('hole_id', holeIds);
        setTeeDistances((distData as HoleTeeDistance[]) ?? []);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load hole data');
    } finally {
      setLoading(false);
    }
  }, [course.id]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    const hole = holes.find((h) => h.hole_number === selectedHole);
    setForm(hole ? holeToForm(hole) : defaultHoleForm());

    if (hole && teeBoxes.length > 0) {
      const tf: TeeDistanceForm = {};
      teeBoxes.forEach((t) => {
        const dist = teeDistances.find((d) => d.hole_id === hole.id && d.tee_box_id === t.id);
        tf[t.id] = {
          yards: dist?.distance_yards != null ? String(dist.distance_yards) : '',
          meters: dist?.distance_meters != null ? String(dist.distance_meters) : '',
        };
      });
      setTeeForm(tf);
    } else {
      setTeeForm(defaultTeeDistanceForm(teeBoxes));
    }
    setDirty(false);
  }, [selectedHole, holes, teeBoxes, teeDistances]);

  const handleChange = (field: keyof HoleForm, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const handleMetersChange = (val: string) => {
    handleChange('distance_meters', val);
    if (val && !isNaN(Number(val))) {
      handleChange('distance_yards', String(Math.round(Number(val) * 1.09361)));
    }
  };

  const handleYardsChange = (val: string) => {
    handleChange('distance_yards', val);
    if (val && !isNaN(Number(val))) {
      handleChange('distance_meters', String(Math.round(Number(val) / 1.09361)));
    }
  };

  const handleTeeYardsChange = (teeId: string, val: string) => {
    setTeeForm((prev) => {
      const meters = val && !isNaN(Number(val)) ? String(Math.round(Number(val) / 1.09361)) : prev[teeId]?.meters ?? '';
      return { ...prev, [teeId]: { yards: val, meters } };
    });
    setDirty(true);
  };

  const handleTeeMetersChange = (teeId: string, val: string) => {
    setTeeForm((prev) => {
      const yards = val && !isNaN(Number(val)) ? String(Math.round(Number(val) * 1.09361)) : prev[teeId]?.yards ?? '';
      return { ...prev, [teeId]: { meters: val, yards } };
    });
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const existing = holes.find((h) => h.hole_number === selectedHole);
      const payload = {
        course_id: course.id,
        hole_number: selectedHole,
        par: form.par,
        handicap_index: form.handicap_index ? Number(form.handicap_index) : null,
        distance_meters: form.distance_meters ? Number(form.distance_meters) : null,
        distance_yards: form.distance_yards ? Number(form.distance_yards) : null,
        description: form.description.trim() || null,
        notes: form.notes.trim() || null,
        updated_at: new Date().toISOString(),
      };

      let holeId: string;

      if (existing) {
        const { data, error } = await supabase
          .from('holes')
          .update(payload)
          .eq('id', existing.id)
          .select()
          .single();
        if (error) throw error;
        setHoles((prev) => prev.map((h) => (h.id === existing.id ? (data as Hole) : h)));
        holeId = existing.id;
      } else {
        const { data, error } = await supabase
          .from('holes')
          .insert(payload)
          .select()
          .single();
        if (error) throw error;
        const newHole = data as Hole;
        setHoles((prev) => [...prev, newHole].sort((a, b) => a.hole_number - b.hole_number));
        holeId = newHole.id;
      }

      if (teeBoxes.length > 0) {
        const upserts = teeBoxes
          .filter((t) => teeForm[t.id]?.yards || teeForm[t.id]?.meters)
          .map((t) => ({
            hole_id: holeId,
            tee_box_id: t.id,
            distance_yards: teeForm[t.id]?.yards ? Number(teeForm[t.id].yards) : null,
            distance_meters: teeForm[t.id]?.meters ? Number(teeForm[t.id].meters) : null,
            updated_at: new Date().toISOString(),
          }));

        if (upserts.length > 0) {
          const { data: upserted, error: uErr } = await supabase
            .from('hole_tee_distances')
            .upsert(upserts, { onConflict: 'hole_id,tee_box_id' })
            .select();
          if (uErr) throw uErr;
          const newDists = (upserted as HoleTeeDistance[]) ?? [];
          setTeeDistances((prev) => {
            const filtered = prev.filter(
              (d) => !newDists.some((nd) => nd.hole_id === d.hole_id && nd.tee_box_id === d.tee_box_id)
            );
            return [...filtered, ...newDists];
          });
        }

        const clearedTeeIds = teeBoxes
          .filter((t) => !teeForm[t.id]?.yards && !teeForm[t.id]?.meters)
          .map((t) => t.id);
        if (clearedTeeIds.length > 0) {
          await supabase
            .from('hole_tee_distances')
            .delete()
            .eq('hole_id', holeId)
            .in('tee_box_id', clearedTeeIds);
          setTeeDistances((prev) =>
            prev.filter((d) => !(d.hole_id === holeId && clearedTeeIds.includes(d.tee_box_id)))
          );
        }
      }

      toast.success(`Hole ${selectedHole} saved`);
      setDirty(false);
    } catch (err) {
      console.error(err);
      toast.error('Failed to save hole');
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (dirty) await handleSave();
    if (selectedHole < totalHoles) setSelectedHole((n) => n + 1);
  };

  const handlePrev = async () => {
    if (dirty) await handleSave();
    if (selectedHole > 1) setSelectedHole((n) => n - 1);
  };

  const completedCount = holes.length;
  const completionPct = Math.round((completedCount / totalHoles) * 100);
  const courseTotalPar = holes.reduce((sum, h) => sum + h.par, 0);
  const front9 = holes.filter((h) => h.hole_number <= 9);
  const back9 = holes.filter((h) => h.hole_number >= 10);
  const currentHoleData = holes.find((h) => h.hole_number === selectedHole);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-golf-gray transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Courses
        </button>
        <div className="h-4 w-px bg-gray-200" />
        <div>
          <h2 className="text-xl font-bold text-golf-gray">{course.name}</h2>
          <p className="text-sm text-gray-500">Course Setup</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-golf-green/10 flex items-center justify-center">
            <TrophyIcon className="w-5 h-5 text-golf-green" />
          </div>
          <div>
            <p className="text-xs text-gray-500">Holes Defined</p>
            <p className="text-xl font-bold text-golf-gray">{completedCount}<span className="text-sm text-gray-400 font-normal">/{totalHoles}</span></p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-golf-orange/10 flex items-center justify-center">
            <span className="text-golf-orange font-bold text-sm">PAR</span>
          </div>
          <div>
            <p className="text-xs text-gray-500">Total Par</p>
            <p className="text-xl font-bold text-golf-gray">{courseTotalPar || course.par}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-gray-500">Completion</p>
            <p className="text-xs font-bold text-golf-gray">{completionPct}%</p>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div
              className="bg-golf-green h-2 rounded-full transition-all duration-500"
              style={{ width: `${completionPct}%` }}
            />
          </div>
        </div>
      </div>

      <div className="flex border-b border-gray-200 bg-white rounded-t-xl overflow-hidden">
        {(['holes', 'tees'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-golf-orange text-golf-orange'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'holes' ? 'Hole Layout' : `Tee Boxes${teeBoxes.length > 0 ? ` (${teeBoxes.length})` : ''}`}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'tees' && (
          <motion.div
            key="tees"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
          >
            <TeeBoxManager
              course={course}
              onTeeBoxesChange={(updated) => setTeeBoxes(updated)}
            />
          </motion.div>
        )}

        {activeTab === 'holes' && (
          <motion.div
            key="holes"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="border-b border-gray-100 px-5 py-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700">Hole Overview</h3>
                <div className="flex items-center gap-3 text-xs text-gray-400">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400 inline-block" /> Par 3</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400 inline-block" /> Par 4</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" /> Par 5</span>
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-golf-orange border-t-transparent" />
                </div>
              ) : (
                <div className="p-4 space-y-3">
                  {totalHoles > 9 && (
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-1">Front 9</p>
                  )}
                  <div className="grid grid-cols-9 gap-2">
                    {Array.from({ length: Math.min(9, totalHoles) }, (_, i) => {
                      const n = i + 1;
                      const hole = holes.find((h) => h.hole_number === n) ?? null;
                      return (
                        <HoleCard key={n} hole={hole} holeNumber={n} isSelected={selectedHole === n} onClick={() => setSelectedHole(n)} />
                      );
                    })}
                  </div>

                  {totalHoles > 9 && (
                    <>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-1 mt-2">Back 9</p>
                      <div className="grid grid-cols-9 gap-2">
                        {Array.from({ length: Math.min(9, totalHoles - 9) }, (_, i) => {
                          const n = i + 10;
                          const hole = holes.find((h) => h.hole_number === n) ?? null;
                          return (
                            <HoleCard key={n} hole={hole} holeNumber={n} isSelected={selectedHole === n} onClick={() => setSelectedHole(n)} />
                          );
                        })}
                      </div>
                    </>
                  )}

                  {totalHoles > 18 && (
                    <>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-1 mt-2">Extra 9</p>
                      <div className="grid grid-cols-9 gap-2">
                        {Array.from({ length: totalHoles - 18 }, (_, i) => {
                          const n = i + 19;
                          const hole = holes.find((h) => h.hole_number === n) ?? null;
                          return (
                            <HoleCard key={n} hole={hole} holeNumber={n} isSelected={selectedHole === n} onClick={() => setSelectedHole(n)} />
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <button onClick={handlePrev} disabled={selectedHole === 1} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                    <ChevronLeftIcon className="w-4 h-4 text-gray-600" />
                  </button>
                  <div>
                    <h3 className="text-lg font-bold text-golf-gray">Hole {selectedHole}</h3>
                    {currentHoleData && (
                      <p className="text-xs text-gray-500">
                        Par {currentHoleData.par}
                        {currentHoleData.distance_yards ? ` · ${currentHoleData.distance_yards} yards` : ''}
                        {currentHoleData.handicap_index ? ` · HCP ${currentHoleData.handicap_index}` : ''}
                      </p>
                    )}
                  </div>
                  <button onClick={handleNext} disabled={selectedHole === totalHoles} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                    <ChevronRightIcon className="w-4 h-4 text-gray-600" />
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  {dirty && (
                    <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1 rounded-lg">Unsaved changes</span>
                  )}
                  <button
                    onClick={handleSave}
                    disabled={saving || !dirty}
                    className="flex items-center gap-1.5 px-4 py-2 bg-golf-orange text-white text-sm font-semibold rounded-lg hover:bg-golf-orange/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <CheckIcon className="w-4 h-4" />
                    )}
                    Save Hole
                  </button>
                </div>
              </div>

              <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Par</label>
                    <div className="flex gap-2">
                      {[3, 4, 5, 6].map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => handleChange('par', p)}
                          className={`flex-1 py-3 rounded-xl font-bold text-sm border-2 transition-all ${
                            form.par === p
                              ? p === 3 ? 'bg-blue-500 text-white border-blue-500' :
                                p === 4 ? 'bg-golf-green text-white border-golf-green' :
                                p === 5 ? 'bg-amber-500 text-white border-amber-500' :
                                          'bg-red-500 text-white border-red-500'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Handicap Index
                      <span className="text-gray-400 font-normal ml-1">(stroke index, 1–18)</span>
                    </label>
                    <input
                      type="number"
                      value={form.handicap_index}
                      onChange={(e) => handleChange('handicap_index', e.target.value)}
                      min={1}
                      max={18}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-sm"
                      placeholder="e.g. 1"
                    />
                  </div>

                  {teeBoxes.length === 0 && (
                    <>
                      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-3 flex items-start gap-2.5">
                        <InformationCircleIcon className="w-4 h-4 text-golf-orange flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-700">Set up tee boxes to enable per-tee distances</p>
                          <p className="text-xs text-gray-500 mt-0.5">Define tee boxes (e.g. Black, Blue, Red) to record a different yardage for each tee on every hole.</p>
                          <button
                            type="button"
                            onClick={() => setActiveTab('tees')}
                            className="mt-2 text-xs font-semibold text-golf-orange hover:underline"
                          >
                            Go to Tee Boxes tab →
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Distance (Yards)</label>
                        <input
                          type="number"
                          value={form.distance_yards}
                          onChange={(e) => handleYardsChange(e.target.value)}
                          min={50}
                          max={700}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-sm"
                          placeholder="e.g. 425"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Distance (Metres)</label>
                        <input
                          type="number"
                          value={form.distance_meters}
                          onChange={(e) => handleMetersChange(e.target.value)}
                          min={50}
                          max={650}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-sm"
                          placeholder="e.g. 389"
                        />
                      </div>
                    </>
                  )}

                  {teeBoxes.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Distances by Tee
                      </label>
                      <div className="space-y-2">
                        {teeBoxes.map((tee) => (
                          <div key={tee.id} className="flex items-center gap-2">
                            <div className={`w-3.5 h-3.5 rounded-full flex-shrink-0 ${COLOR_DOT[tee.color] ?? 'bg-gray-200'}`} />
                            <span className="text-xs font-medium text-gray-700 w-28 truncate">{tee.name}</span>
                            <input
                              type="number"
                              value={teeForm[tee.id]?.yards ?? ''}
                              onChange={(e) => handleTeeYardsChange(tee.id, e.target.value)}
                              min={50}
                              max={700}
                              className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-xs"
                              placeholder="yds"
                            />
                            <span className="text-xs text-gray-400">yd</span>
                            <input
                              type="number"
                              value={teeForm[tee.id]?.meters ?? ''}
                              onChange={(e) => handleTeeMetersChange(tee.id, e.target.value)}
                              min={50}
                              max={650}
                              className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-xs"
                              placeholder="mts"
                            />
                            <span className="text-xs text-gray-400">m</span>
                          </div>
                        ))}
                      </div>
                      {teeBoxes.length > 0 && (
                        <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                          <InformationCircleIcon className="w-3.5 h-3.5" />
                          Yards and metres auto-convert when you type in one field
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={form.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-sm resize-none"
                      placeholder="Describe the hole layout, hazards, features..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes
                      <span className="text-gray-400 font-normal ml-1">(internal)</span>
                    </label>
                    <textarea
                      value={form.notes}
                      onChange={(e) => handleChange('notes', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-sm resize-none"
                      placeholder="Internal notes for officials..."
                    />
                  </div>

                  {form.distance_yards && form.distance_meters && teeBoxes.length === 0 && (
                    <div className="bg-golf-sand/30 rounded-xl p-3 flex items-start gap-2">
                      <InformationCircleIcon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-gray-500">
                        {form.distance_yards} yards = {form.distance_meters} metres
                        {form.par === 3 && Number(form.distance_yards) > 250 && ' · Long par 3'}
                        {form.par === 4 && Number(form.distance_yards) < 350 && ' · Short par 4'}
                        {form.par === 5 && Number(form.distance_yards) > 600 && ' · Reachable in two for long hitters'}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-t border-gray-100">
                <button
                  onClick={handlePrev}
                  disabled={selectedHole === 1}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                  Previous Hole
                </button>
                <span className="text-xs text-gray-400">{selectedHole} of {totalHoles}</span>
                <button
                  onClick={handleNext}
                  disabled={selectedHole === totalHoles}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  Next Hole
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {holes.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-700">Scorecard Summary</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Hole</th>
                        {front9.map((h) => (
                          <th
                            key={h.hole_number}
                            onClick={() => setSelectedHole(h.hole_number)}
                            className={`text-center px-2 py-2.5 text-xs font-semibold uppercase tracking-wider cursor-pointer transition-colors ${
                              selectedHole === h.hole_number ? 'text-golf-orange' : 'text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            {h.hole_number}
                          </th>
                        ))}
                        <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-100">Out</th>
                        {back9.map((h) => (
                          <th
                            key={h.hole_number}
                            onClick={() => setSelectedHole(h.hole_number)}
                            className={`text-center px-2 py-2.5 text-xs font-semibold uppercase tracking-wider cursor-pointer transition-colors ${
                              selectedHole === h.hole_number ? 'text-golf-orange' : 'text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            {h.hole_number}
                          </th>
                        ))}
                        {back9.length > 0 && (
                          <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-100">In</th>
                        )}
                        <th className="text-center px-3 py-2.5 text-xs font-semibold text-golf-gray uppercase tracking-wider bg-gray-100">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100">
                        <td className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Par</td>
                        {front9.map((h) => (
                          <td key={h.hole_number} className={`text-center px-2 py-2.5 font-medium text-sm ${
                            h.par === 3 ? 'text-blue-600' : h.par === 5 ? 'text-amber-600' : 'text-gray-700'
                          }`}>
                            {h.par}
                          </td>
                        ))}
                        <td className="text-center px-3 py-2.5 font-bold text-sm bg-gray-50">
                          {front9.reduce((s, h) => s + h.par, 0) || '—'}
                        </td>
                        {back9.map((h) => (
                          <td key={h.hole_number} className={`text-center px-2 py-2.5 font-medium text-sm ${
                            h.par === 3 ? 'text-blue-600' : h.par === 5 ? 'text-amber-600' : 'text-gray-700'
                          }`}>
                            {h.par}
                          </td>
                        ))}
                        {back9.length > 0 && (
                          <td className="text-center px-3 py-2.5 font-bold text-sm bg-gray-50">
                            {back9.reduce((s, h) => s + h.par, 0)}
                          </td>
                        )}
                        <td className="text-center px-3 py-2.5 font-bold text-sm bg-gray-50 text-golf-gray">
                          {courseTotalPar || '—'}
                        </td>
                      </tr>

                      {teeBoxes.length > 0 ? (
                        teeBoxes.map((tee) => (
                          <tr key={tee.id} className="border-b border-gray-100">
                            <td className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              <div className="flex items-center gap-1.5">
                                <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${COLOR_DOT[tee.color] ?? 'bg-gray-200'}`} />
                                {tee.name}
                              </div>
                            </td>
                            {front9.map((h) => {
                              const dist = teeDistances.find((d) => d.hole_id === h.id && d.tee_box_id === tee.id);
                              return (
                                <td key={h.hole_number} className="text-center px-2 py-2.5 text-xs text-gray-500">
                                  {dist?.distance_yards ?? '—'}
                                </td>
                              );
                            })}
                            <td className="text-center px-3 py-2.5 text-xs font-bold text-gray-600 bg-gray-50">
                              {(() => {
                                const total = front9.reduce((s, h) => {
                                  const d = teeDistances.find((dd) => dd.hole_id === h.id && dd.tee_box_id === tee.id);
                                  return s + (d?.distance_yards ?? 0);
                                }, 0);
                                return total || '—';
                              })()}
                            </td>
                            {back9.map((h) => {
                              const dist = teeDistances.find((d) => d.hole_id === h.id && d.tee_box_id === tee.id);
                              return (
                                <td key={h.hole_number} className="text-center px-2 py-2.5 text-xs text-gray-500">
                                  {dist?.distance_yards ?? '—'}
                                </td>
                              );
                            })}
                            {back9.length > 0 && (
                              <td className="text-center px-3 py-2.5 text-xs font-bold text-gray-600 bg-gray-50">
                                {(() => {
                                  const total = back9.reduce((s, h) => {
                                    const d = teeDistances.find((dd) => dd.hole_id === h.id && dd.tee_box_id === tee.id);
                                    return s + (d?.distance_yards ?? 0);
                                  }, 0);
                                  return total || '—';
                                })()}
                              </td>
                            )}
                            <td className="text-center px-3 py-2.5 text-xs font-bold text-gray-600 bg-gray-50">
                              {(() => {
                                const total = holes.reduce((s, h) => {
                                  const d = teeDistances.find((dd) => dd.hole_id === h.id && dd.tee_box_id === tee.id);
                                  return s + (d?.distance_yards ?? 0);
                                }, 0);
                                return total || '—';
                              })()}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <>
                          <tr className="border-b border-gray-100">
                            <td className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Yards</td>
                            {front9.map((h) => (
                              <td key={h.hole_number} className="text-center px-2 py-2.5 text-xs text-gray-500">
                                {h.distance_yards ?? '—'}
                              </td>
                            ))}
                            <td className="text-center px-3 py-2.5 text-xs font-bold text-gray-600 bg-gray-50">
                              {front9.reduce((s, h) => s + (h.distance_yards ?? 0), 0) || '—'}
                            </td>
                            {back9.map((h) => (
                              <td key={h.hole_number} className="text-center px-2 py-2.5 text-xs text-gray-500">
                                {h.distance_yards ?? '—'}
                              </td>
                            ))}
                            {back9.length > 0 && (
                              <td className="text-center px-3 py-2.5 text-xs font-bold text-gray-600 bg-gray-50">
                                {back9.reduce((s, h) => s + (h.distance_yards ?? 0), 0)}
                              </td>
                            )}
                            <td className="text-center px-3 py-2.5 text-xs font-bold text-gray-600 bg-gray-50">
                              {holes.reduce((s, h) => s + (h.distance_yards ?? 0), 0) || '—'}
                            </td>
                          </tr>
                        </>
                      )}

                      <tr>
                        <td className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">HCP</td>
                        {front9.map((h) => (
                          <td key={h.hole_number} className="text-center px-2 py-2.5 text-xs text-gray-500">
                            {h.handicap_index ?? '—'}
                          </td>
                        ))}
                        <td className="bg-gray-50" />
                        {back9.map((h) => (
                          <td key={h.hole_number} className="text-center px-2 py-2.5 text-xs text-gray-500">
                            {h.handicap_index ?? '—'}
                          </td>
                        ))}
                        {back9.length > 0 && <td className="bg-gray-50" />}
                        <td className="bg-gray-50" />
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pb-4">
              <button
                onClick={onBack}
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-golf-gray transition-colors"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Back to Courses
              </button>
              {completedCount < totalHoles && (
                <button
                  onClick={async () => {
                    for (let n = 1; n <= totalHoles; n++) {
                      const exists = holes.find((h) => h.hole_number === n);
                      if (!exists) {
                        await supabase.from('holes').insert({
                          course_id: course.id,
                          hole_number: n,
                          par: 4,
                          updated_at: new Date().toISOString(),
                        });
                      }
                    }
                    fetchAll();
                    toast.success('All holes initialized with par 4');
                  }}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-golf-green border border-golf-green/30 bg-golf-green/5 hover:bg-golf-green/10 rounded-lg transition-colors"
                >
                  <PlusIcon className="w-4 h-4" />
                  Initialize All Holes (Par 4)
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
